// Migrations of the dashboard file from each older schema version to the next (parse.ts runs them).
// Each works on the file's JSON as it was in its version, and must not change once released.

type Json = Record<string, unknown>;

function isRecord(value: unknown) : value is Json {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * The units version 1 could convert between (the legacy `UnitsProvider`), by lower-case key: a
 * temperature by name, a length or speed by its factor to metres or metres per second.
 */
const LINEAR: Readonly<Record<string, { dimension: 'length' | 'speed'; factor: number }>> = {
    m: { dimension: 'length', factor: 1 },
    km: { dimension: 'length', factor: 1000 },
    cm: { dimension: 'length', factor: 0.01 },
    mm: { dimension: 'length', factor: 0.001 },
    ft: { dimension: 'length', factor: 0.3048 },
    in: { dimension: 'length', factor: 0.0254 },
    yd: { dimension: 'length', factor: 0.9144 },
    mi: { dimension: 'length', factor: 1609.344 },
    ms: { dimension: 'speed', factor: 1 },
    kmh: { dimension: 'speed', factor: 1000 / 3600 },
    mph: { dimension: 'speed', factor: 1609.344 / 3600 },
    kt: { dimension: 'speed', factor: 1852 / 3600 },
};

/** `expression` (a transform, or `value`) to Celsius from `unit`, and from Celsius to `unit`. */
const TEMPERATURE: Readonly<Record<string, { toC: (e: string) => string; fromC: (e: string) => string }>> = {
    c: { toC: (e) => e, fromC: (e) => e },
    f: { toC: (e) => `ftoc(${e})`, fromC: (e) => `ctof(${e})` },
    k: { toC: (e) => `${operand(e)}-273.15`, fromC: (e) => `${operand(e)}+273.15` },
};

/** In parentheses where an operator follows it, unless it is a bare name such as `value`. */
function operand(expression: string) : string {
    return /^[A-Za-z_][A-Za-z0-9_]*$/.test(expression) ? expression : `(${expression})`;
}

/**
 * A transform that gives what version 1 showed: `transform` (or the plain value), then converted
 * from `from` to `to`. Unchanged where nothing was converted; undefined where version 1 could not
 * convert, which showed no number.
 */
export function transformWithConversion(transform: string | undefined, from: string | undefined, to: string | undefined) : string | undefined | null {
    if (from === undefined || to === undefined || from === to) {
        return transform;
    }

    const value = transform ?? 'value';
    const [source, target] = [from.toLowerCase(), to.toLowerCase()];
    const [fromT, toT] = [TEMPERATURE[source], TEMPERATURE[target]];
    if (fromT && toT) {
        return source === target ? transform : toT.fromC(fromT.toC(value));
    }

    const [fromL, toL] = [LINEAR[source], LINEAR[target]];
    if (fromL && toL && fromL.dimension === toL.dimension) {
        return source === target ? transform : `${operand(value)}*${Number((fromL.factor / toL.factor).toPrecision(10))}`;
    }

    return null;
}

/**
 * Version 1 to 2 (tiles spec TU-1 to TU-4, issue #3): units are text only. A value tile's list of
 * units becomes its first unit's text, and the conversion that unit made goes into the transform;
 * a table cell's conversion likewise; data sources lose the unit they reported in.
 */
function unitsAsText(json: Json) : Json {
    const screens = Array.isArray(json.screens) ? json.screens : [];

    return {
        ...json,
        schemaVersion: 2,
        screens: screens.map((screen) => {
            if (!isRecord(screen) || !Array.isArray(screen.tiles)) {
                return screen;
            }

            return { ...screen, tiles: screen.tiles.map((tile) => (isRecord(tile) ? tileWithUnitsAsText(tile) : tile)) };
        }),
    };
}

/** A data source without its unit. */
function withoutUnit(source: unknown) : unknown {
    if (!isRecord(source)) {
        return source;
    }
    const { unit: _unit, ...rest } = source;

    return rest;
}

function tileWithUnitsAsText(tile: Json) : Json {
    const sources = isRecord(tile.dataSources) ? tile.dataSources : undefined;
    // `dataSource` is short for `dataSources.primary`.
    const shortSource = isRecord(tile.dataSource) ? tile.dataSource : undefined;
    const unitOf = (name: unknown) : string | undefined => {
        const source = typeof name === 'string' ? (sources?.[name] ?? (name === 'primary' ? shortSource : undefined)) : undefined;

        return isRecord(source) && typeof source.unit === 'string' ? source.unit : undefined;
    };
    const written: Json = { ...tile };
    if (sources) {
        written.dataSources = Object.fromEntries(Object.entries(sources).map(([name, source]) => [name, withoutUnit(source)]));
    }
    if (shortSource) {
        written.dataSource = withoutUnit(shortSource);
    }

    const options = isRecord(tile.options) ? tile.options : undefined;
    if (!options) {
        return written;
    }

    if (tile.type === 'value' && 'units' in options) {
        const { units, ...rest } = options;
        const first = Array.isArray(units) && isRecord(units[0]) ? units[0] : undefined;
        const next: Json = { ...rest };
        if (typeof first?.text === 'string' && first.text !== '') {
            next.unit = first.text;
        }
        // A gauge never converted its value.
        if (!isRecord(options.gauge)) {
            setTransform(next, typeof options.transform === 'string' ? options.transform : undefined, unitOf(options.source ?? 'primary'), typeof first?.convertTo === 'string' ? first.convertTo : undefined);
        }
        written.options = next;
    }

    if (tile.type === 'table' && Array.isArray(options.rows)) {
        written.options = {
            ...options,
            rows: options.rows.map((row) => (Array.isArray(row)
                ? row.map((cell) => {
                    if (!isRecord(cell) || !('unit' in cell)) {
                        return cell;
                    }
                    const { unit, ...rest } = cell;
                    setTransform(rest, typeof cell.transform === 'string' ? cell.transform : undefined, unitOf(cell.source), typeof unit === 'string' ? unit : undefined);

                    return rest;
                })
                : row)),
        };
    }

    return written;
}

/**
 * Writes the transform that converts as version 1 did. A conversion version 1 did not know (it
 * showed no number) is dropped, and the transform kept as it was.
 */
function setTransform(target: Json, transform: string | undefined, from: string | undefined, to: string | undefined) : void {
    const converted = transformWithConversion(transform, from, to);
    if (typeof converted === 'string') {
        target.transform = converted;
    }
}

/**
 * Version 2 to 3 (dashboard-editor spec §5.9, issue #12): a tile's `colors` become its `style`,
 * and the dashboard's `tileColors` its `tileStyle`, with the same keys. Groups get no style of
 * their own: they take the dashboard's like any tile (the owner, QT-1).
 */
function colorsAsStyle(json: Json) : Json {
    const renamed = (tile: unknown) : unknown => {
        if (!isRecord(tile)) {
            return tile;
        }
        const { colors, ...rest } = tile;
        const next: Json = colors === undefined ? rest : { ...rest, style: colors };
        // A group as Phase 2 T7 wrote it may still hold tiles.
        if (Array.isArray(next.tiles)) {
            next.tiles = next.tiles.map(renamed);
        }

        return next;
    };
    const { tileColors, ...rest } = json;
    const screens = Array.isArray(json.screens) ? json.screens : [];

    return {
        ...rest,
        ...(tileColors === undefined ? {} : { tileStyle: tileColors }),
        schemaVersion: 3,
        screens: screens.map((screen) => (isRecord(screen) && Array.isArray(screen.tiles) ? { ...screen, tiles: screen.tiles.map(renamed) } : screen)),
    };
}

/**
 * Version 3 to 4 (tiles spec §3 SP-8, issue #13): a value tile's `graph` becomes its `sparkline`,
 * with the first series' source and colour and the graph's line width; the source is left out where
 * it is the value's own. Other series, bars, running totals and labels are dropped.
 */
function graphAsSparkline(json: Json) : Json {
    const renamed = (tile: unknown) : unknown => {
        if (!isRecord(tile)) {
            return tile;
        }
        const next: Json = { ...tile };
        if (Array.isArray(next.tiles)) {
            next.tiles = next.tiles.map(renamed);
        }
        const options = isRecord(tile.options) ? tile.options : undefined;
        if (tile.type !== 'value' || !options || !('graph' in options)) {
            return next;
        }

        const { graph, ...rest } = options;
        const first = isRecord(graph) && Array.isArray(graph.series) && isRecord(graph.series[0]) ? graph.series[0] : {};
        const sparkline: Json = {};
        const own = typeof options.source === 'string' ? options.source : 'primary';
        if (typeof first.source === 'string' && first.source !== own) {
            sparkline.source = first.source;
        }
        if (typeof first.color === 'string' && first.color.toLowerCase() !== '#ffffff') {
            sparkline.color = first.color;
        }
        if (isRecord(graph) && typeof graph.lineWidth === 'number' && graph.lineWidth !== 1) {
            sparkline.lineWidth = graph.lineWidth;
        }
        next.options = { ...rest, sparkline };

        return next;
    };
    const screens = Array.isArray(json.screens) ? json.screens : [];

    return {
        ...json,
        schemaVersion: 4,
        screens: screens.map((screen) => (isRecord(screen) && Array.isArray(screen.tiles) ? { ...screen, tiles: screen.tiles.map(renamed) } : screen)),
    };
}

export const MIGRATIONS: Readonly<Record<number, (json: Json) => Json>> = {
    1: unitsAsText,
    2: colorsAsStyle,
    3: graphAsSparkline,
};
